import {
  Combobox,
  type ComboboxOption
} from '@library/components/selects/combobox/Combobox';
import {
  createPlayerOptions,
  sortPlayersByPositionAndName
} from '@bo-utils/simulations';
import { useMemo, useState } from 'react';
import { Button } from '@library/components/buttons';
import type { LeagueTeamPlayer } from '@library/types/player';
import type { ModifiedPlayer } from '@library/types/simulations';
import type { Updater } from 'use-immer';
import chevronDownIcon from '@library/assets/images/icons/chevron-down-icon.svg';
import { classNames } from '@library/utils';
import { difference } from 'ramda';

export const RemovedPlayers = ({
  homeTeam,
  removedPlayers,
  setRemovedPlayers,
  teamPlayers
}: {
  homeTeam: boolean;
  removedPlayers: Array<Omit<ModifiedPlayer, 'playerOverrides'>>;
  setRemovedPlayers: Updater<Array<Omit<ModifiedPlayer, 'playerOverrides'>>>;
  teamPlayers: Array<LeagueTeamPlayer>;
}) => {
  const [showRemovedPlayers, setShowTeamOverrides] = useState(true);

  const formattedTeamPlayers = useMemo(() => {
    const sortedTeamPlayers = sortPlayersByPositionAndName({
      players: teamPlayers
    });

    const players = sortedTeamPlayers.map((teamPlayer) => {
      const { foreign_id: foreignId, name: playerName } = teamPlayer || {};

      return { foreignId, homeTeam, playerId: foreignId, playerName };
    });

    return players;
  }, [homeTeam, teamPlayers]);

  const teamPlayersComboboxOptions = useMemo(
    () => createPlayerOptions({ players: formattedTeamPlayers }),
    [formattedTeamPlayers]
  );

  const teamRemovedPlayers = useMemo(
    () => removedPlayers.filter((player) => player.homeTeam === homeTeam),
    [homeTeam, removedPlayers]
  );

  const removedPlayerOptions = useMemo(() => {
    return createPlayerOptions({ players: teamRemovedPlayers });
  }, [teamRemovedPlayers]);

  const handleRemovePlayersChange = (
    newValues: Array<ComboboxOption> | null
  ) => {
    const newRemovedPlayers =
      newValues && newValues.length
        ? formattedTeamPlayers.filter((player) =>
            newValues.some(
              (removedPlayer) => removedPlayer.value === player.foreignId
            )
          )
        : [];

    const newSelected = difference(newRemovedPlayers, teamRemovedPlayers);
    const newUnselected = difference(teamRemovedPlayers, newRemovedPlayers);

    setRemovedPlayers((draft) => {
      newUnselected.forEach((removedPlayer) => {
        const removedPlayerIndex = draft.findIndex(
          (draftRemovedPlayer) =>
            draftRemovedPlayer.foreignId === removedPlayer.foreignId &&
            draftRemovedPlayer.homeTeam === removedPlayer.homeTeam
        );

        if (removedPlayerIndex !== -1) {
          draft.splice(removedPlayerIndex, 1);
        }
      });

      newSelected.forEach((removedPlayer) => {
        draft.push(removedPlayer);
      });
    });
  };

  return (
    <div
      className={classNames(
        'border-soFresh-140 inline-flex w-full flex-col items-start justify-start rounded border'
      )}
    >
      <div className="bg-soFresh-120 border-soFresh-140 font-heebo text-soClean-100 box-border flex w-full flex-row items-center justify-between rounded p-3 text-base font-medium leading-6">
        <span>Remove Players</span>
        <div className="flex gap-4">
          <Button
            type="button"
            title="ToggleGameTeamPlayersButton"
            onClick={() => setShowTeamOverrides(!showRemovedPlayers)}
          >
            <img
              alt="Chevron Down icon"
              className={classNames(showRemovedPlayers ? 'rotate-180' : '')}
              src={chevronDownIcon}
            />
          </Button>
        </div>
      </div>
      {showRemovedPlayers && (
        <div className="border-soFresh-140 w-full p-4">
          <div className="w-full">
            <Combobox
              id="players"
              isMulti
              label=""
              value={removedPlayerOptions}
              placeholder="Select Players..."
              onChange={handleRemovePlayersChange}
              options={teamPlayersComboboxOptions}
              isClearable
            />
          </div>
        </div>
      )}
    </div>
  );
};
